import avatars from '@/assets/avatars'
import type {
  GetContentModuleResponse,
  GetSidekicksResponse,
  GetSponsorTopicsResponse,
  TopicResponse,
} from '@/services/api/api.types'

import type { SidekickOption } from './useSidekicks'

// Utility function for sorting topics
export const sortTopicsByPriority = (topics: GetSponsorTopicsResponse) =>
  [...topics].sort((a, b) => (b.priority ?? 100) - (a.priority ?? 100))

// Calculate priorities more efficiently
export const calculatePriorities = (topics: GetSponsorTopicsResponse) => {
  const maxIndex = Math.max(1, topics.length - 1)
  return topics.map((topic, index) => ({
    ...topic,
    priority: Math.round(100 * (1 - index / maxIndex)),
  }))
}

// Helper function to find the next available number for duplicated topic names
export const findDuplicateNumber = (topics: TopicResponse[], baseName: string): number => {
  let number = 1
  const namePattern = new RegExp(`^${baseName} \\((\\d+)\\)$`)

  // Check existing topics for matching names
  while (
    topics.some(topic => {
      const match = topic.displayName.match(namePattern)
      return match && parseInt(match[1]) === number
    })
  ) {
    number++
  }

  return number
}

// Helper function to compare subtopics
export const compareSubtopics = (
  subtopics1?: TopicResponse['subtopics'],
  subtopics2?: TopicResponse['subtopics'],
): boolean => {
  // Handle null/undefined cases
  if (!subtopics1 && !subtopics2) return true
  if (!subtopics1 || !subtopics2) return false
  if (subtopics1.length !== subtopics2.length) return false

  const sorted1 = [...subtopics1].sort((a, b) => (a.id || '').localeCompare(b.id || ''))
  const sorted2 = [...subtopics2].sort((a, b) => (a.id || '').localeCompare(b.id || ''))

  // Compare each subtopic
  return sorted1.every((subtopic1, index) => {
    const subtopic2 = sorted2[index]
    const idMatch = subtopic1.id === subtopic2.id
    const nameMatch = subtopic1.displayName === subtopic2.displayName
    return idMatch && nameMatch
  })
}

// Helper function to compare two individual topics (excluding priority - handled at array level)
export const compareTopic = (topic1: TopicResponse, topic2: TopicResponse): boolean => {
  const idMatch = topic1.id === topic2.id
  const nameMatch = topic1.displayName === topic2.displayName
  const subtopicsMatch = compareSubtopics(topic1.subtopics, topic2.subtopics)
  const skIdMatch = topic1.skId === topic2.skId
  const notRemovableMatch = topic1.notRemovable === topic2.notRemovable

  const overallMatch = idMatch && nameMatch && subtopicsMatch && skIdMatch && notRemovableMatch

  return overallMatch
}

// Helper function to compare two topic arrays comprehensively
export const compareTopicArrays = (topics1: GetSponsorTopicsResponse, topics2: GetSponsorTopicsResponse): boolean => {
  if (topics1.length !== topics2.length) return false

  // Check that the array order is the same by comparing IDs at each position
  const arrayOrderMatches = topics1.every((topic1, index) => {
    const topic2 = topics2[index]
    return topic1.id === topic2.id
  })

  if (!arrayOrderMatches) {
    return false
  }

  // Now compare individual topics (excluding priority since order comparison handles that)
  const sorted1 = [...topics1].sort((a, b) => (a.id || '').localeCompare(b.id || ''))
  const sorted2 = [...topics2].sort((a, b) => (a.id || '').localeCompare(b.id || ''))

  return sorted1.every((topic1, index) => {
    const topic2 = sorted2[index]
    return compareTopic(topic1, topic2)
  })
}

// Utility function to poll data until it matches expected state or times out
export const pollUntilDataMatches = async function <T>(
  refetchFn: () => Promise<T>,
  compareFn: (serverData: T) => boolean,
  options: {
    maxAttempts?: number
    intervalMs?: number
    timeoutMs?: number
  } = {},
): Promise<T> {
  const {
    maxAttempts = 30,
    intervalMs = 200,
    timeoutMs = 10000, // 10 second watchdog
  } = options

  const startTime = Date.now()
  let attempts = 0

  while (attempts < maxAttempts && Date.now() - startTime < timeoutMs) {
    try {
      const data = await refetchFn()
      if (compareFn(data)) {
        return data
      }
    } catch (error) {
      console.warn('Poll attempt failed:', error)
    }

    attempts++
    if (attempts < maxAttempts && Date.now() - startTime < timeoutMs) {
      await new Promise(resolve => setTimeout(resolve, intervalMs))
    }
  }

  // If we reach here, polling timed out or max attempts reached
  console.warn('Polling timed out or max attempts reached', {
    attempts,
    timeElapsed: Date.now() - startTime,
    maxAttempts,
    timeoutMs,
  })

  // Return the last fetched data even if it doesn't match
  return await refetchFn()
}

// Helper function to find duplicate names while preserving original casing
export const findDuplicateNames = (items: { displayName: string }[]): string[] => {
  const nameMap: Record<string, string[]> = {}
  const duplicates: string[] = []

  items.forEach(item => {
    const trimmed = item.displayName.trim()
    const lowerName = trimmed.toLowerCase()

    if (lowerName) {
      if (!nameMap[lowerName]) {
        nameMap[lowerName] = []
      }
      nameMap[lowerName].push(trimmed)

      // When we reach 2 items with the same lowercase name, add all variants as duplicates
      if (nameMap[lowerName].length === 2) {
        duplicates.push(...nameMap[lowerName])
      } else if (nameMap[lowerName].length > 2) {
        // Add any additional variants
        duplicates.push(trimmed)
      }
    }
  })

  return duplicates
}

// Validate topics for empty names, duplicate names, and subtopic names
export const validateTopics = (topics: TopicResponse[]): string[] => {
  const errors: string[] = []

  // Check for empty topic names
  const emptyTopics = topics.filter(topic => !topic.displayName.trim())
  if (emptyTopics.length > 0) {
    errors.push('Topic names cannot be empty')
  }

  // Check for duplicate topic names
  const duplicateTopics = findDuplicateNames(topics)
  if (duplicateTopics.length > 0) {
    errors.push(`Duplicate topic names found: ${duplicateTopics.join(', ')}`)
  }

  // Check for duplicate subtopic names within each topic
  const topicsWithDuplicateSubtopics: string[] = []
  topics.forEach(topic => {
    if (topic.subtopics && topic.subtopics.length > 0) {
      const duplicateSubtopics = findDuplicateNames(topic.subtopics)
      if (duplicateSubtopics.length > 0) {
        topicsWithDuplicateSubtopics.push(`${topic.displayName} (${duplicateSubtopics.join(', ')})`)
      }
    }
  })

  if (topicsWithDuplicateSubtopics.length > 0) {
    errors.push(`Duplicate subtopic names found in: ${topicsWithDuplicateSubtopics.join('; ')}`)
  }

  return errors
}

// Count how many content modules are associated with each topic
export const calculateTopicModuleCounts = (
  topics: TopicResponse[],
  modules: GetContentModuleResponse[],
): Record<string, number> => {
  const counts: Record<string, number> = {}

  // Initialize counts with 0 for all topics
  topics.forEach(topic => {
    if (topic.id) {
      counts[topic.id] = 0
    }
  })

  // Count modules for each topic using the topics field
  modules
    .filter(module => module.status !== 'removed') // exclude removed modules
    .forEach(module => {
      if (module.topics?.length) {
        module.topics.forEach(topicId => {
          if (topicId && counts.hasOwnProperty(topicId)) {
            counts[topicId] = (counts[topicId] || 0) + 1
          }
        })
      }
    })

  return counts
}

// Determine which topics should show warnings (have modules and changes)
export const calculateTopicWarnings = (
  currentTopics: TopicResponse[],
  originalTopics: TopicResponse[],
  topicModuleCounts: Record<string, number>,
): Set<string> => {
  const warnings = new Set<string>()

  currentTopics.forEach(topic => {
    const hasModules = topicModuleCounts[topic.id ?? ''] > 0
    const originalTopic = originalTopics.find(t => t.id === topic.id)
    const hasNameChange = originalTopic && topic.displayName !== originalTopic.displayName
    const hasSidekickChange = originalTopic && topic.skId !== originalTopic.skId

    if (hasModules && (hasNameChange || hasSidekickChange)) {
      warnings.add(topic.id ?? '')
    }
  })

  return warnings
}

// MSK (Member Sidekick) Utility Functions
// ============================================================

/**
 * Sorts sidekicks with initially selected MSKs at top, default first
 */
export function sortSidekicksForMskSelection(
  sidekicks: SidekickOption[],
  initialSelectedMsks: SidekickOption[],
  initialDefaultMsk: string | null,
): SidekickOption[] {
  return [...sidekicks].sort((a, b) => {
    const aInitiallySelected = initialSelectedMsks.some(msk => msk.name === a.name)
    const bInitiallySelected = initialSelectedMsks.some(msk => msk.name === b.name)
    const aIsInitialDefault = initialDefaultMsk === a.id || initialDefaultMsk === a.name
    const bIsInitialDefault = initialDefaultMsk === b.id || initialDefaultMsk === b.name

    // 1. Initial default MSK comes first
    if (aIsInitialDefault && !bIsInitialDefault) return -1
    if (!aIsInitialDefault && bIsInitialDefault) return 1

    // 2. Initially selected MSKs come before unselected
    if (aInitiallySelected && !bInitiallySelected) return -1
    if (!aInitiallySelected && bInitiallySelected) return 1

    // 3. Within same selection status, sort by availability (available first)
    if (a.isAvailable && !b.isAvailable) return -1
    if (!a.isAvailable && b.isAvailable) return 1

    // 4. Finally sort by name
    return a.name.localeCompare(b.name)
  })
}

/**
 * Validates MSK selection meets business requirements
 */
export function validateMskSelection(
  selectedCount: number,
  defaultMsk: string | null,
  minSelections: number,
  maxSelections: number,
): boolean {
  return selectedCount >= minSelections && selectedCount <= maxSelections && defaultMsk !== null
}

/**
 * Detects if MSK selections have changed from saved state
 */
export function hasMskChanges(
  savedSelectedMsks: SidekickOption[],
  currentSelectedMsks: SidekickOption[],
  savedDefaultMsk: string | null,
  currentDefaultMsk: string | null,
  selectedMskNames: Set<string>,
): boolean {
  // Check if number of selections changed
  if (savedSelectedMsks.length !== currentSelectedMsks.length) {
    return true
  }

  // Check if default changed
  if (savedDefaultMsk !== currentDefaultMsk) {
    return true
  }

  // Check if any selections changed
  const savedNames = new Set(savedSelectedMsks.map(msk => msk.name))
  return (
    savedSelectedMsks.some(msk => !selectedMskNames.has(msk.name)) ||
    currentSelectedMsks.some(msk => !savedNames.has(msk.name))
  )
}

/**
 * Creates a Set of names from saved MSKs for state reset
 */
export function createMskNamesSet(msks: SidekickOption[]): Set<string> {
  return new Set(msks.map(msk => msk.name))
}

/**
 * Gets a consistent identifier for a sidekick, handling the messy backend data structure
 * Priority: type > id > _id > name (fallback to empty string)
 */
export function getSidekickId(sidekick: SidekickOption): string {
  return sidekick.type || sidekick.id || sidekick._id || sidekick.name || ''
}

/**
 * Gets the default PSK (Personal Sidekick) from sidekicks data
 */
export function getDefaultPsk(sidekicksData?: GetSidekicksResponse) {
  return sidekicksData?.mskTypes?.find((msk: any) => msk.default) || null
}

/**
 * Gets the avatar ID for the default PSK, with fallback to placeholder
 */
export function getDefaultPskAvatarId(sidekicksData?: GetSidekicksResponse): string {
  const defaultPsk = getDefaultPsk(sidekicksData)
  return defaultPsk?.avatarId || 'placeholder'
}

/**
 * Gets the avatar image for the default PSK, with fallback to placeholder
 */
export function getDefaultPskAvatar(sidekicksData?: GetSidekicksResponse): string {
  const avatarId = getDefaultPskAvatarId(sidekicksData)
  return avatars[avatarId] || avatars.placeholder
}
